/**
 * Print-pipeline readiness helpers.
 *
 * The PDF generator opens /printpma/... in a headless browser. It should wait
 * until window.__PMA_PRINT_READY__ === true (or
 * document.documentElement.dataset.pmaPrintReady === "true") before capturing.
 *
 * Until then the page keeps showing "Loading PMA..." — including when fonts
 * fail to load in time (we intentionally do NOT mark ready on timeout).
 */

export const PMA_PRINT_READY_FLAG = "__PMA_PRINT_READY__";
export const PMA_PRINT_READY_ATTR = "pmaPrintReady";

/** Faces used heavily on PMA print layouts — force-load so capture isn't racing Typekit. */
export const CRITICAL_PMA_PRINT_FONTS = [
  "400 16px freight-big-pro",
  "300 italic 16px freight-big-pro",
  "600 italic 16px freight-big-pro",
  "400 16px objektiv-mk2",
  "500 16px objektiv-mk2",
  "700 16px objektiv-mk2",
  "400 16px objektiv-mk3",
  "400 16px playfair-display",
  "700 16px playfair-display",
  "italic 16px playfair-display",
  "200 16px mr-eaves-xl-modern",
  "400 16px mr-eaves-xl-modern",
  "400 italic 16px mr-eaves-xl-modern",
  "200 16px mr-eaves-xl-modern-narrow",
  "400 16px mr-eaves-xl-modern-narrow",
  "400 16px mr-eaves-xl-sans-narrow",
  "400 16px caecilia-sans-text",
  "700 16px caecilia-sans-head",
  "400 16px utopia-std",
  "italic 16px utopia-std",
  "400 16px quasimoda",
  "400 16px alex-brush",
  "400 16px ivypresto-headline",
  "700 16px ivypresto-headline",
  "400 16px mencken-std-head",
];

export const CRITICAL_PDF_PRINT_FONTS = [
  "200 16px Maitree",
  "300 16px Maitree",
  "400 16px Maitree",
  "500 16px Maitree",
  "600 16px Maitree",
  "700 16px Maitree",
  "400 20px usps4cb",
];

const DEFAULT_TIMEOUT_MS = 15000;

/**
 * Force-load critical PMA faces, then wait for document.fonts.ready.
 * Rejects (via timedOut/ok:false) if fonts do not finish within timeoutMs so
 * the print page can keep showing "Loading PMA..." and never signal ready.
 */
export async function waitForPrintFonts({
  timeoutMs = DEFAULT_TIMEOUT_MS,
  fontSpecs = CRITICAL_PMA_PRINT_FONTS,
} = {}) {
  if (typeof document === "undefined" || !document.fonts) {
    return { ok: true, skipped: true, timedOut: false };
  }

  const loadCritical = Promise.all(
    fontSpecs.map((spec) =>
      document.fonts.load(spec).catch((err) => {
        console.warn(`[pmaPrintReady] failed to load "${spec}"`, err);
        return null;
      })
    )
  );

  const fontsReady =
    typeof document.fonts.ready !== "undefined"
      ? document.fonts.ready
      : Promise.resolve();

  let timeoutId;
  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
  });

  const loadPromise = Promise.all([loadCritical, fontsReady])
    .then(() => ({ timedOut: false }))
    .catch((error) => {
      console.warn("[pmaPrintReady] error while waiting for print fonts", error);
      return { timedOut: false, error };
    });

  const result = await Promise.race([loadPromise, timeoutPromise]);
  clearTimeout(timeoutId);

  if (result.timedOut) {
    console.warn(
      `[pmaPrintReady] timed out waiting for print fonts after ${timeoutMs}ms — keeping Loading PMA...`
    );
    return { ok: false, timedOut: true, error: null };
  }

  if (result.error) {
    return { ok: false, timedOut: false, error: result.error };
  }

  return { ok: true, timedOut: false, error: null };
}

export function markPmaPrintReady() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  window[PMA_PRINT_READY_FLAG] = true;
  document.documentElement.dataset[PMA_PRINT_READY_ATTR] = "true";
}

export function clearPmaPrintReady() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  window[PMA_PRINT_READY_FLAG] = false;
  delete document.documentElement.dataset[PMA_PRINT_READY_ATTR];
}

/**
 * Class-component helpers: start font wait on mount, signal ready after paint.
 * On font timeout, fontsReady stays false → page keeps "Loading PMA..." and
 * __PMA_PRINT_READY__ is never set (backend should timeout/retry).
 *
 * Usage:
 *   componentDidMount() { startPrintFontGate(this); ... }
 *   componentDidUpdate() { signalPrintReadyIfIdle(this, this.isPrintDataReady()); }
 *   componentWillUnmount() { stopPrintFontGate(this); }
 * Gate render on: isDataReady() && this.state.fontsReady
 */
export function startPrintFontGate(component, options) {
  clearPmaPrintReady();
  component.setState({ fontsReady: false, fontsTimedOut: false });
  component._pmaPrintFontsUnmounted = false;
  component._pmaPrintReadySignaled = false;

  waitForPrintFonts(options).then((result) => {
    if (component._pmaPrintFontsUnmounted) return;

    if (result.ok) {
      if (typeof window !== "undefined") {
        window.__PMA_PRINT_FONTS_TIMED_OUT__ = false;
      }
      component.setState({ fontsReady: true, fontsTimedOut: false });
      return;
    }

    // Fail closed: do not mark fontsReady. Stay on "Loading PMA..." so PDF
    // capture cannot proceed with missing fonts.
    if (typeof window !== "undefined") {
      window.__PMA_PRINT_FONTS_TIMED_OUT__ = true;
    }
    component.setState({ fontsReady: false, fontsTimedOut: true });
    clearPmaPrintReady();
  });
}

export function stopPrintFontGate(component) {
  component._pmaPrintFontsUnmounted = true;
  component._pmaPrintReadySignaled = false;
  clearPmaPrintReady();
}

export function signalPrintReadyIfIdle(component, isDataReady) {
  const fontsReady = !!(component.state && component.state.fontsReady);
  if (isDataReady && fontsReady) {
    if (component._pmaPrintReadySignaled) return;
    component._pmaPrintReadySignaled = true;
    // Wait one frame so the PMA DOM is painted with loaded faces.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!component._pmaPrintFontsUnmounted) {
          markPmaPrintReady();
        }
      });
    });
  } else {
    component._pmaPrintReadySignaled = false;
    clearPmaPrintReady();
  }
}
